// AGC: TimelineUnified - Unified vertical timeline showing tasks, notes, and phenological stages
// AGC: Replaces separate LotTasksCard + LotTimeline with a single integrated view

import React, { useMemo, useRef, useEffect, useState } from 'react'
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Camera,
  Video,
  Mic,
  FileText,
  Sprout,
  Droplets,
  FlaskConical,
  Wind,
  Wheat,
  Wrench,
  ChevronDown,
  ChevronUp,
  Play,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useLotTimeline } from '../../hooks/useLotTimeline'
import type { TimelineEntry } from '../../types/lotNotes'
import type { GrowthStage } from '../../types/plotTimeline'

interface TimelineUnifiedProps {
  lotId: string
  plantingDate?: string
  stages?: GrowthStage[]
  isExpanded?: boolean
  onToggleExpand?: () => void
}

// AGC: Task category icon mapping
const getCategoryIcon = (category?: string) => {
  switch (category) {
    case 'planting': return <Sprout className="w-4 h-4" />
    case 'irrigation': return <Droplets className="w-4 h-4" />
    case 'fertilizing': return <FlaskConical className="w-4 h-4" />
    case 'spraying': return <Wind className="w-4 h-4" />
    case 'harvest': return <Wheat className="w-4 h-4" />
    case 'maintenance': return <Wrench className="w-4 h-4" />
    default: return <Circle className="w-4 h-4" />
  }
}

// AGC: Note type icon mapping
const getNoteIcon = (type?: string) => {
  switch (type) {
    case 'photo': return <Camera className="w-4 h-4" />
    case 'video': return <Video className="w-4 h-4" />
    case 'audio': return <Mic className="w-4 h-4" />
    case 'text': return <FileText className="w-4 h-4" />
    default: return <FileText className="w-4 h-4" />
  }
}

// AGC: Task status styling
const getStatusStyle = (entry: TimelineEntry) => {
  if (entry.type !== 'task') return { color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800' }

  const task = entry.taskData
  const status = task?.status || 'pending'
  const dueDate = task?.dueDate ? new Date(task.dueDate) : null
  const isOverdue = dueDate && dueDate < new Date() && status !== 'completed'

  if (status === 'completed') return { color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800' }
  if (isOverdue) return { color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800' }
  if (status === 'in-progress') return { color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800' }
  return { color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-700' }
}

// AGC: Get status icon
const getStatusIcon = (entry: TimelineEntry) => {
  if (entry.type !== 'task') return null
  const task = entry.taskData
  const status = task?.status || 'pending'
  const dueDate = task?.dueDate ? new Date(task.dueDate) : null
  const isOverdue = dueDate && dueDate < new Date() && status !== 'completed'

  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-green-500" />
  if (isOverdue) return <AlertCircle className="w-4 h-4 text-red-500" />
  if (status === 'in-progress') return <Clock className="w-4 h-4 text-amber-500" />
  return <Circle className="w-4 h-4 text-gray-400" />
}

// AGC: Format date for display — accepts t() for i18n since this is outside the component
const formatDate = (dateStr: string, t: (key: string, params?: Record<string, unknown>) => string) => {
  const date = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const entryDate = new Date(date)
  entryDate.setHours(0, 0, 0, 0)

  const diffDays = Math.round((entryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return t('common.today')
  if (diffDays === -1) return t('common.yesterday')
  if (diffDays === 1) return t('common.tomorrow')
  if (diffDays > 1 && diffDays <= 7) return t('lotPage.inDays', { count: diffDays })
  if (diffDays < -1 && diffDays >= -7) return t('lotPage.daysAgo', { count: Math.abs(diffDays) })

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

// AGC: Determine current phenological stage
const getCurrentStage = (plantingDate: string, stages: GrowthStage[]): GrowthStage | null => {
  if (!plantingDate || stages.length === 0) return null
  const planting = new Date(plantingDate)
  const today = new Date()
  const daysSincePlanting = Math.floor((today.getTime() - planting.getTime()) / (1000 * 60 * 60 * 24))

  let current: GrowthStage | null = null
  for (const stage of stages) {
    if (daysSincePlanting >= stage.startDay) {
      current = stage
    }
  }
  return current
}

/**
 * AGC: TimelineUnified - Vertical timeline with tasks, notes, and stage markers
 */
export const TimelineUnified: React.FC<TimelineUnifiedProps> = ({
  lotId,
  plantingDate,
  stages = [],
  isExpanded = true,
  onToggleExpand,
}) => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<'all' | 'tasks' | 'notes'>('all')
  const { timeline, isLoading, noteCount, taskCount } = useLotTimeline(lotId, filter === 'all' ? 'all' : filter)
  const todayRef = useRef<HTMLDivElement>(null)

  // AGC: Current stage
  const currentStage = useMemo(() => {
    if (!plantingDate) return null
    return getCurrentStage(plantingDate, stages)
  }, [plantingDate, stages])

  // AGC: Sort timeline by planned date (tasks use dueDate, notes use timestamp)
  const sortedTimeline = useMemo(() => {
    const getDate = (e: TimelineEntry) => {
      if (e.type === 'task' && e.taskData?.dueDate) return new Date(e.taskData.dueDate).getTime()
      if (e.type === 'task' && e.taskData?.startDate) return new Date(e.taskData.startDate).getTime()
      return new Date(e.timestamp).getTime()
    }
    return [...timeline].sort((a, b) => getDate(a) - getDate(b))
  }, [timeline])

  // AGC: Scroll to today marker on mount
  useEffect(() => {
    if (isExpanded && todayRef.current) {
      todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [isExpanded])

  // AGC: Stage progress bar
  const stageProgress = useMemo(() => {
    if (!plantingDate || stages.length === 0) return 0
    const planting = new Date(plantingDate)
    const today = new Date()
    const daysSincePlanting = Math.floor((today.getTime() - planting.getTime()) / (1000 * 60 * 60 * 24))
    const lastStage = stages[stages.length - 1]
    const totalDays = (lastStage.endDay || lastStage.startDay) + 30
    return Math.min(100, Math.max(0, (daysSincePlanting / totalDays) * 100))
  }, [plantingDate, stages])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* AGC: Header with stage progress */}
      <button
        onClick={onToggleExpand}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sprout className="w-5 h-5 text-green-600" />
          <span className="font-semibold text-sm text-gray-900 dark:text-white">
            {t('lotPage.timeline')}
          </span>
          {currentStage && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
              {currentStage.code} - {currentStage.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{timeline.length} {t('common.items', 'items')}</span>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* AGC: Phenological stage progress bar */}
      {stages.length > 0 && (
        <div className="px-4 pb-2">
          <div className="relative h-6 flex items-center">
            {/* AGC: Background track */}
            <div className="absolute inset-x-0 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full" />
            {/* AGC: Progress fill */}
            <div
              className="absolute left-0 h-1.5 bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all"
              style={{ width: `${stageProgress}%` }}
            />
            {/* AGC: Stage markers */}
            {stages.map((stage) => {
              const lastStage = stages[stages.length - 1]
              const totalDays = (lastStage.endDay || lastStage.startDay) + 30
              const position = (stage.startDay / totalDays) * 100
              const isCurrent = currentStage?.code === stage.code
              return (
                <div
                  key={stage.code}
                  className="absolute -translate-x-1/2"
                  style={{ left: `${Math.min(97, position)}%` }}
                  title={`${stage.code} - ${stage.name} (dia ${stage.startDay})`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full border-2 ${
                    isCurrent
                      ? 'bg-green-500 border-green-600 ring-2 ring-green-200 dark:ring-green-800'
                      : stageProgress >= position
                        ? 'bg-green-400 border-green-500'
                        : 'bg-gray-300 dark:bg-gray-600 border-gray-400 dark:border-gray-500'
                  }`} />
                  {isCurrent && (
                    <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
                      {stage.code}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          {/* AGC: Stage labels */}
          <div className="flex justify-between mt-3 px-1">
            <span className="text-[9px] text-gray-400">{stages[0]?.code}</span>
            <span className="text-[9px] text-gray-400">{stages[stages.length - 1]?.code}</span>
          </div>
        </div>
      )}

      {/* AGC: Filter tabs */}
      {isExpanded && (
        <div className="flex gap-1 px-4 pb-2">
          {[
            { key: 'all' as const, label: t('common.all') },
            { key: 'tasks' as const, label: `${t('tasks.tasks')}${taskCount ? ` (${taskCount})` : ''}` },
            { key: 'notes' as const, label: `${t('notes.notes')}${noteCount ? ` (${noteCount})` : ''}` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* AGC: Timeline entries */}
      {isExpanded && (
        <div className="px-4 pb-4 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sortedTimeline.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Sprout className="w-8 h-8 text-gray-300 dark:text-gray-600" />
              </div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t('lotPage.noActivityRecorded')}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {t('lotPage.noActivityHint')}
              </p>
            </div>
          ) : (
            <div className="relative">
              {/* AGC: Vertical timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />

              {sortedTimeline.map((entry, index) => {
                const style = getStatusStyle(entry)
                const isToday = formatDate(entry.timestamp, t) === t('common.today')

                return (
                  <div key={entry.id} className="relative pl-10 pb-4" ref={isToday && index === 0 ? todayRef : undefined}>
                    {/* AGC: Timeline dot */}
                    <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 ${
                      entry.type === 'task'
                        ? entry.taskData?.status === 'completed'
                          ? 'bg-green-500 border-green-600'
                          : 'bg-white dark:bg-gray-800 border-gray-400'
                        : 'bg-blue-500 border-blue-600'
                    }`} />

                    {/* AGC: Today indicator */}
                    {isToday && index === 0 && (
                      <div className="absolute left-0 right-0 -top-1 flex items-center gap-2 mb-2">
                        <div className="h-px flex-1 bg-red-400" />
                        <span className="text-[10px] font-bold text-red-500 uppercase">{t('common.today')}</span>
                        <div className="h-px flex-1 bg-red-400" />
                      </div>
                    )}

                    {/* AGC: Entry card */}
                    <div className={`rounded-xl border p-3 ${style.bg} ${style.border} transition-colors`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {/* AGC: Type icon */}
                          <div className={`flex-shrink-0 ${style.color}`}>
                            {entry.type === 'task' ? getCategoryIcon(entry.taskData?.category) : getNoteIcon(entry.noteData?.type)}
                          </div>
                          {/* AGC: Title */}
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {entry.title}
                          </span>
                        </div>
                        {/* AGC: Status icon for tasks */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {getStatusIcon(entry)}
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {formatDate(entry.timestamp, t)}
                          </span>
                        </div>
                      </div>
                      {/* AGC: Note content preview */}
                      {entry.type === 'note' && entry.noteData && (
                        <div className="mt-2 pl-6">
                          {/* AGC: Photo thumbnail */}
                          {entry.noteData.type === 'photo' && entry.noteData.mediaUrl && (
                            <img
                              src={entry.noteData.mediaThumbnailUrl || entry.noteData.mediaUrl}
                              alt="Foto"
                              className="w-20 h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
                            />
                          )}
                          {/* AGC: Video thumbnail */}
                          {entry.noteData.type === 'video' && entry.noteData.mediaUrl && (
                            <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                              {entry.noteData.mediaThumbnailUrl ? (
                                <img
                                  src={entry.noteData.mediaThumbnailUrl}
                                  alt="Vídeo"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                  <Video className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-8 h-8 bg-black/50 rounded-full flex items-center justify-center">
                                  <Play className="w-4 h-4 text-white ml-0.5" />
                                </div>
                              </div>
                              {entry.noteData.mediaDuration != null && isFinite(entry.noteData.mediaDuration) && entry.noteData.mediaDuration > 0 && (
                                <span className="absolute bottom-1 right-1 text-[10px] bg-black/60 text-white px-1 rounded">
                                  {Math.floor(entry.noteData.mediaDuration)}s
                                </span>
                              )}
                            </div>
                          )}
                          {/* AGC: Audio duration */}
                          {entry.noteData.type === 'audio' && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Play className="w-3 h-3" />
                              <span>{entry.noteData.mediaDuration != null && isFinite(entry.noteData.mediaDuration) && entry.noteData.mediaDuration > 0 ? `${Math.floor(entry.noteData.mediaDuration)}s` : t('notes.audioNote')}</span>
                            </div>
                          )}
                          {/* AGC: Text preview */}
                          {entry.noteData.type === 'text' && entry.noteData.content && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 italic">
                              "{entry.noteData.content}"
                            </p>
                          )}
                          {/* AGC: Author */}
                          {entry.userName && (
                            <p className="text-[10px] text-gray-400 mt-1">{t('common.by')} {entry.userName}</p>
                          )}
                        </div>
                      )}
                      {/* AGC: Task description */}
                      {entry.type === 'task' && entry.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 pl-6">
                          {entry.description}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TimelineUnified
